/**
 * Fase 2A · P0-3 · GestorOnboarding
 * Blueprint §7.3.3 · 3 pasos: bienvenida · datos · código para compartir con clientes.
 * Fase 2D · Ampliado con paso 3 "código de gestor" · usa POST /api/me/gestor-codes existente.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import OnboardingWizard from "../../components/onboarding/OnboardingWizard";
import { api } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";
import { Checkbox } from "../../components/ui/checkbox";
import { Scales, CheckCircle, Receipt, Sparkle, Copy, Buildings } from "@phosphor-icons/react";
import { toast } from "sonner";

const PERMS = [
  { key: "customers", label: "Clientes", desc: "Ver listado y datos operativos" },
  { key: "providers", label: "Proveedores", desc: "Gastos y facturas de proveedores" },
  { key: "expenses", label: "Gastos", desc: "Registro y aprobación" },
  { key: "tax", label: "AEAT / Impuestos", desc: "M303, M111 y cierres trimestrales" },
  { key: "analytics", label: "Analíticas", desc: "KPIs y dashboards fiscales" },
];

export default function GestorOnboarding() {
  const navigate = useNavigate();

  const steps = [
    {
      id: "welcome",
      label: "Datos gestor",
      render: ({ data, updateData }) => (
        <div className="space-y-4">
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-[var(--ag-gold)]">
            <Scales weight="fill" /> Gestor / Asesor
          </div>
          <h2 className="font-display text-2xl font-black tracking-tighter">
            Bienvenido, gestor
          </h2>
          <p className="text-sm text-[var(--ag-text-dim)]">
            Vas a tener acceso solo lectura a los datos fiscales del centro que te ha invitado. AutoGlow
            te permite exportar el cierre trimestral con un clic (M303 · M111 · servicios · gastos).
          </p>
          <div>
            <Label className="text-xs uppercase tracking-wider text-[var(--ag-text-dim)]">Nombre / Despacho</Label>
            <Input
              value={data.name || ""}
              onChange={(e) => updateData({ name: e.target.value })}
              placeholder="Ej. Laura Gestora · Asesoría Fiscal SA"
              className="mt-1"
              data-testid="gestor-onb-name"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-[var(--ag-text-dim)]">Email profesional</Label>
            <Input
              type="email"
              value={data.email || ""}
              onChange={(e) => updateData({ email: e.target.value })}
              placeholder="gestor@despacho.com"
              className="mt-1"
              data-testid="gestor-onb-email"
            />
          </div>
        </div>
      ),
    },
    {
      id: "permissions",
      label: "Confirmación de acceso",
      render: ({ data, updateData }) => {
        const perms = data.permissions || { customers: true, providers: true, expenses: true, tax: true, analytics: false };
        const toggle = (k) => updateData({ permissions: { ...perms, [k]: !perms[k] } });
        return (
          <div className="space-y-4">
            <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-[var(--ag-gold)]">
              <Receipt weight="fill" /> Alcance del acceso
            </div>
            <h2 className="font-display text-xl font-bold">Áreas que quieres ver</h2>
            <p className="text-sm text-[var(--ag-text-dim)]">
              El titular podrá revisar y ajustar estos permisos después. Puedes cambiar tu selección más tarde.
            </p>
            <div className="space-y-2">
              {PERMS.map((p) => (
                <label
                  key={p.key}
                  className="flex items-start gap-3 p-3 border border-[var(--ag-border)] rounded cursor-pointer hover:bg-[var(--ag-midnight)]/50"
                  data-testid={`gestor-onb-perm-${p.key}`}
                >
                  <Checkbox
                    checked={!!perms[p.key]}
                    onCheckedChange={() => toggle(p.key)}
                    className="mt-0.5"
                  />
                  <div>
                    <div className="text-sm font-semibold">{p.label}</div>
                    <div className="text-[11px] text-[var(--ag-text-dim)]">{p.desc}</div>
                  </div>
                </label>
              ))}
            </div>
            <div className="p-3 bg-emerald-500/10 border border-emerald-500/30 rounded flex items-start gap-2">
              <CheckCircle size={14} weight="fill" className="text-emerald-400 mt-0.5" />
              <div className="text-xs text-emerald-200">
                Al finalizar tendrás acceso a tu panel gestor y podrás exportar el trimestre en XLSX/ZIP.
              </div>
            </div>
          </div>
        );
      },
    },
    {
      id: "code",
      label: "Tu código de gestor",
      render: () => <StepGestorCode />,
    },
  ];

  return (
    <OnboardingWizard
      roleKey="gestor"
      eyebrow="Onboarding · Gestor"
      title="Alta como gestor fiscal"
      subtitle="3 pasos rápidos y ya podrás gestionar los centros de tus clientes."
      steps={steps}
      homeRoute="/login"
      homeCta="Iniciar sesión"
      onComplete={() => navigate("/login")}
      onSkip={() => navigate("/login")}
    />
  );
}

/* ─────── Fase 2D · StepGestorCode ─────── */
function StepGestorCode() {
  const { user } = useAuth() || {};
  const [codes, setCodes] = useState(null);
  const [creating, setCreating] = useState(false);

  const isLoggedGestor = user?.role === "gestor";

  const load = async () => {
    if (!isLoggedGestor) { setCodes([]); return; }
    try {
      const { data } = await api.get("/me/gestor-codes");
      setCodes(data.items || []);
    } catch { setCodes([]); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [user?.id]);

  const createCode = async () => {
    if (!isLoggedGestor) {
      toast.info("Inicia sesión primero para generar códigos");
      return;
    }
    setCreating(true);
    try {
      await api.post("/me/gestor-codes", { note: "Onboarding" });
      toast.success("Código generado");
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "No se pudo crear el código");
    } finally { setCreating(false); }
  };

  const copy = (code) => {
    try {
      navigator.clipboard.writeText(code);
      toast.success("Código copiado al portapapeles");
    } catch { toast.error("No se pudo copiar"); }
  };

  return (
    <div className="space-y-4" data-testid="gestor-onb-step-code">
      <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-[var(--ag-gold)]">
        <Sparkle weight="fill" /> Tu código de gestor
      </div>
      <h2 className="font-display text-xl font-bold">Cómo empezar a gestionar centros</h2>
      <p className="text-sm text-[var(--ag-text-dim)]">
        Comparte este código con tus clientes titulares. Ellos lo canjean desde su panel y automáticamente
        aparecerá el centro en tu vista consolidada de gestor.
      </p>

      {!isLoggedGestor ? (
        <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded text-xs text-amber-200">
          Inicia sesión con tu cuenta de gestor para generar tu primer código.
        </div>
      ) : codes === null ? (
        <div className="p-4 border border-[var(--ag-border)] rounded animate-pulse text-xs text-[var(--ag-text-dim)]">Cargando…</div>
      ) : codes.length === 0 ? (
        <div className="p-4 border border-dashed border-[var(--ag-border)] rounded text-center">
          <Buildings size={28} weight="duotone" className="text-[var(--ag-gold)] mx-auto" />
          <div className="text-sm text-[var(--ag-text)] mt-2">Aún no tienes códigos activos.</div>
          <Button
            type="button"
            onClick={createCode}
            disabled={creating}
            className="ag-btn-gold mt-3 h-9 rounded-sm text-xs"
            data-testid="gestor-onb-create-code"
          >
            {creating ? "Generando…" : "Generar mi primer código"}
          </Button>
        </div>
      ) : (
        <div className="space-y-2" data-testid="gestor-onb-codes-list">
          {codes.slice(0, 3).map((c) => (
            <div key={c.code} className="flex items-center justify-between gap-2 p-3 border border-[var(--ag-border)] rounded bg-[var(--ag-midnight)]/40">
              <div className="min-w-0 flex-1">
                <div className="font-mono-tab font-bold text-lg text-[var(--ag-gold)] tracking-widest">{c.code}</div>
                {c.note && <div className="text-[10px] text-[var(--ag-text-dim)] mt-0.5 truncate">{c.note}</div>}
              </div>
              <Button
                type="button"
                onClick={() => copy(c.code)}
                variant="outline"
                size="sm"
                className="border-[var(--ag-border)] bg-transparent text-[var(--ag-text)] hover:text-[var(--ag-gold)] rounded-sm h-8"
                data-testid={`gestor-onb-code-copy-${c.code}`}
              >
                <Copy size={12} className="mr-1" weight="bold" /> Copiar
              </Button>
            </div>
          ))}
          {codes.length < 5 && (
            <Button
              type="button"
              onClick={createCode}
              disabled={creating}
              variant="outline"
              className="w-full h-9 border-[var(--ag-border)] bg-transparent text-[var(--ag-text-dim)] hover:text-[var(--ag-gold)] rounded-sm text-xs"
              data-testid="gestor-onb-create-code-more"
            >
              + Generar otro código
            </Button>
          )}
        </div>
      )}

      <div className="p-3 bg-emerald-500/10 border border-emerald-500/30 rounded flex items-start gap-2">
        <CheckCircle size={14} weight="fill" className="text-emerald-400 mt-0.5" />
        <div className="text-xs text-emerald-200">
          Al finalizar tendrás acceso a tu panel gestor multi-centro y podrás exportar cada trimestre en XLSX/ZIP.
        </div>
      </div>
    </div>
  );
}
